import { localeRequestHeaders } from './localePreference';
import { apiErrorMessage } from './apiErrorMessages';

const API_PREFIX = '/api/v1';

async function apiRequest(path, user, options = {}) {
  const token = await user.getIdToken();
  const {
    timeoutMs = 30000,
    timeoutMessage = apiErrorMessage('mockTimeout'),
    networkMessage = apiErrorMessage('mockNetwork'),
    ...fetchOptions
  } = options;
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  let response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      ...fetchOptions,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...localeRequestHeaders(),
        ...(fetchOptions.headers || {}),
      },
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error(timeoutMessage);
    }
    throw new Error(networkMessage);
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || apiErrorMessage('mockRequest'));
  }
  return response.json();
}

export function createMockInterviewSession({ user, jobId }) {
  return apiRequest('/mock-interview/session', user, {
    method: 'POST',
    timeoutMs: 45000,
    timeoutMessage: apiErrorMessage('mockPreparingTimeout'),
    body: JSON.stringify({ interviewType: 'structured', jobId }),
  });
}

export function cancelMockInterviewSession({ user, sessionId, reason }) {
  return apiRequest(`/mock-interview/sessions/${encodeURIComponent(sessionId)}/cancel`, user, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || 'voice_session_failed_to_start' }),
  });
}

export function createMockInterviewReport({
  user,
  interviewType,
  sessionId,
  jobDescription,
  transcript,
  durationSeconds,
  truncated,
  endedByAgent,
  completionReason,
  locale,
}) {
  return apiRequest('/mock-interview/report', user, {
    method: 'POST',
    keepalive: true,
    timeoutMs: 180000,
    timeoutMessage: apiErrorMessage('mockReportTimeout'),
    body: JSON.stringify({
      interviewType,
      sessionId,
      jobDescription,
      transcript,
      durationSeconds,
      truncated,
      endedByAgent,
      completionReason,
      locale,
    }),
  });
}

export function listMockInterviewReports({ user, limit = 10 }) {
  return apiRequest(`/mock-interview/reports?limit=${encodeURIComponent(limit)}`, user, {
    method: 'GET',
  });
}

export function getMockInterviewReport({ user, reportId }) {
  return apiRequest(`/mock-interview/reports/${encodeURIComponent(reportId)}`, user, {
    method: 'GET',
  });
}

export function retryMockInterviewReport({ user, reportId }) {
  return apiRequest(`/mock-interview/reports/${encodeURIComponent(reportId)}/retry`, user, {
    method: 'POST',
    timeoutMs: 180000,
    timeoutMessage: apiErrorMessage('mockRetryTimeout'),
  });
}
